<template>
	<div>
		<div class="flex flex-row flex-wrap justify-between items-center gap-2 mb-2">
			<h5 :class="titleClass">Materiale si greutate</h5>
			<div class="flex flex-row items-center gap-4">
				<span v-if="bom" class="text-lg">
					Total <span class="font-bold">{{ kg(bom.total_kg) }} kg</span>
				</span>
				<v-btn :color="bom ? 'blue-grey' : 'teal'" class="uppercase white-text" :loading="loading"
					@click="load">
					<v-icon class="mr-1">{{ bom ? "mdi-refresh" : "mdi-calculator" }}</v-icon>
					<span>{{ bom ? "Recalculeaza" : "Calculeaza" }}</span>
				</v-btn>
			</div>
		</div>

		<p v-if="!bom && !error" class="text-sm text-gray-600 mb-0">
			Lista de materiale si greutatea, calculate din modulele de gard ale ofertei.
		</p>
		<v-alert v-if="error" type="error" outlined dense class="mb-0">{{ error }}</v-alert>

		<template v-if="bom">
			<v-alert v-if="bom.warnings.length" type="warning" outlined dense class="mb-3">
				<span class="font-bold">Totalul nu este complet.</span>
				<ul class="mb-0 pl-4 list-disc">
					<li v-for="(warning, i) in bom.warnings" :key="i">{{ warning.message }}</li>
				</ul>
			</v-alert>

			<v-tabs v-model="tab" color="teal" class="mb-2">
				<v-tab>Pe materiale</v-tab>
				<v-tab>Pe module</v-tab>
			</v-tabs>

			<v-tabs-items v-model="tab">
				<!-- Summed over every module: what to order -->
				<v-tab-item>
					<p v-if="!bom.materials.length" class="text-sm text-gray-600 py-2 mb-0">Nu exista materiale.</p>
					<v-simple-table v-else dense>
						<thead>
							<tr>
								<th>Material</th>
								<th>Detalii</th>
								<th class="text-right">Cantitate</th>
								<th class="text-right">kg / u.m.</th>
								<th class="text-right">Greutate</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in bom.materials" :key="row.code">
								<td>
									<span class="font-bold">{{ row.name }}</span>
									<span v-if="row.supplier_code" class="text-xs text-gray-500 ml-1">{{ row.supplier_code }}</span>
								</td>
								<td>{{ row.details || "-" }}</td>
								<td class="text-right whitespace-nowrap">
									{{ quantity(row.quantity, row.unit) }}
									<span v-if="row.unit === 'ml'" class="text-xs text-gray-500">({{ row.pieces }} buc)</span>
								</td>
								<td class="text-right whitespace-nowrap">
									<template v-if="row.kg_per_unit !== null">
										{{ row.kg_per_unit }}
										<span v-if="row.kg_source === 'formula'" class="text-xs text-gray-500"
											title="Calculat din sectiune, nu din catalogul furnizorului">≈</span>
									</template>
									<span v-else class="text-gray-500">-</span>
								</td>
								<td class="text-right whitespace-nowrap">
									<template v-if="row.kg !== null">{{ kg(row.kg) }} kg</template>
									<span v-else class="orange--text" title="Fara greutate pe unitate">?</span>
								</td>
							</tr>
						</tbody>
						<tfoot>
							<tr>
								<td colspan="4" class="text-right font-bold">Total</td>
								<td class="text-right font-bold whitespace-nowrap">{{ kg(bom.total_kg) }} kg</td>
							</tr>
						</tfoot>
					</v-simple-table>
				</v-tab-item>

				<!-- Per module: what each one is cut from -->
				<v-tab-item>
					<v-expansion-panels multiple accordion flat>
						<v-expansion-panel v-for="(module, i) in bom.modules" :key="i">
							<v-expansion-panel-header>
								<div class="flex flex-row flex-wrap items-center gap-x-4 gap-y-1">
									<span class="font-bold">{{ module.nr }}. {{ moduleLabel(module) }}</span>
									<span v-if="module.model" class="text-gray-600">{{ module.model }}</span>
									<span v-if="module.width || module.height" class="text-gray-600">
										{{ moduleSize(module) }} mm
									</span>
									<span class="text-gray-600">{{ module.quantity }} buc</span>
									<v-icon v-if="module.warnings.length" small color="orange">mdi-alert</v-icon>
								</div>
								<span class="text-right font-bold flex-grow-0 mr-2 whitespace-nowrap">
									{{ module.supported ? kg(module.kg_total) + " kg" : "-" }}
								</span>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<ul v-if="module.warnings.length" class="orange--text text--darken-2 text-sm mb-2 pl-4 list-disc">
									<li v-for="(warning, w) in module.warnings" :key="w">{{ warning.message }}</li>
								</ul>
								<v-simple-table v-if="module.lines.length" dense>
									<thead>
										<tr>
											<th>Element</th>
											<th>Material</th>
											<th class="text-right">Lungime</th>
											<th class="text-right">Buc</th>
											<th class="text-right">Cantitate</th>
											<th class="text-right">Greutate</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(line, l) in module.lines" :key="l">
											<td>{{ roleLabel(line.role) }}</td>
											<td>{{ line.name }}</td>
											<td class="text-right whitespace-nowrap">
												{{ line.length !== null ? line.length + " mm" : "-" }}
											</td>
											<td class="text-right">{{ line.count }}</td>
											<td class="text-right whitespace-nowrap">{{ quantity(line.quantity, line.unit) }}</td>
											<td class="text-right whitespace-nowrap">
												{{ line.kg !== null ? kg(line.kg) + " kg" : "-" }}
											</td>
										</tr>
									</tbody>
									<tfoot>
										<tr>
											<td colspan="6" class="text-right text-sm">
												1 buc: <span class="font-bold">{{ kg(module.kg_each) }} kg</span>
												&middot; {{ module.quantity }} buc:
												<span class="font-bold">{{ kg(module.kg_total) }} kg</span>
											</td>
										</tr>
									</tfoot>
								</v-simple-table>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-tab-item>
			</v-tabs-items>
		</template>
	</div>
</template>

<script>
import Vue from "vue";
import { moduleTypeLabel, variantsFor } from "@/utils/fenceModules";

const authHeader = process.env.VUE_APP_REQUEST_HEADER;
const headers = {};
headers[authHeader] = process.env.VUE_APP_HUB_API_KEY;

/** What each piece does in the module, as production calls it. */
const ROLES = {
	slat: "Lamele",
	u_profile: "Profil U",
	frame: "Rama",
	corner: "Coltare",
	jamb: "Toc",
	cover_strip: "Masca de rost",
	roller_cover: "Mascare rola",
	support_triangle: "Triunghi sustinere",
	reinforcement: "Ranforsare",
	post: "Stalp",
	hinge: "Balamale",
	lock: "Inchidere",
	rivet: "Popnituri",
	screw: "Suruburi",
};

/**
 * Bill of materials and weight for an offer's fence modules, from the hub API's FenceBom.
 *
 * Give it either an offer or a work (which resolves to its offer). It loads on demand - the button -
 * unless `auto` is set, and always reflects the modules as they are saved now.
 */
export default {
	name: "FenceBom",
	props: {
		offerId: { type: [Number, String], default: null },
		workId: { type: [Number, String], default: null },
		auto: { type: Boolean, default: false },
		// so the card's heading matches the page it sits on
		titleClass: { type: String, default: "text-xl" },
	},
	data() {
		return {
			bom: null,
			loading: false,
			error: "",
			tab: 0,
		};
	},
	computed: {
		url() {
			const host = process.env.VUE_APP_HUB_API_HOST;

			if (this.workId) return host + `api/manufacturing/works/${this.workId}/bom`;
			if (this.offerId) return host + `api/manufacturing/offers/${this.offerId}/bom`;

			return null;
		},
	},
	watch: {
		// a different offer or work: what was shown belongs to the previous one
		url() {
			this.bom = null;
			this.error = "";
			if (this.auto) this.load();
		},
	},
	created() {
		if (this.auto) this.load();
	},
	methods: {
		load() {
			if (!this.url) return;

			this.loading = true;
			this.error = "";

			Vue.http.get(this.url, { headers })
				.then(response => {
					this.bom = response.body;
				})
				.catch(error => {
					this.error = (error.body && error.body.error) || "Lista de materiale nu a putut fi calculata.";
				})
				.finally(() => {
					this.loading = false;
				});
		},
		kg(value) {
			return Number(value || 0).toFixed(2);
		},
		quantity(value, unit) {
			return unit === "ml" ? `${Number(value).toFixed(2)} ml` : `${Number(value)} ${unit}`;
		},
		roleLabel(role) {
			return ROLES[role] || role;
		},
		moduleLabel(module) {
			const variant = variantsFor(module.type).find(item => item.value == module.variant);

			return moduleTypeLabel(module.type) + (variant ? ` ${variant.label}` : "");
		},
		moduleSize(module) {
			return module.type === "Stalp" ? `H ${module.height}` : `${module.width} x ${module.height}`;
		},
	},
};
</script>
